import type {ChangeEvent, ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {EventWizardOrganiserOptions} from '@Components/event-wizard/event-wizard-organiser-options';
import type {OrganiserDetails} from '@Components/event-wizard/event-wizard-organisers/event-wizard-organisers.types';
import {organiserImgTypeDropdownID} from '@Components/event-wizard/event-wizard-organisers/event-wizard-organisers.types';
import {Text, TextSize} from '@Components/text';
import {InputField} from '@Components/input-field-v2';
import {InputFieldSize, InputFieldState, InputFieldType} from '@Components/input-field-v2/input-field.types';
import {useEventWizardOrganisers, useEventWizardShouldCheckErrState} from '@Components/event-wizard/event-wizard.hooks';
import {useDispatch} from 'react-redux';
import {onEventWizardSelectedOrganiserUpdate, onEventWizardShouldNextBtnBeDisabled} from '@Components/event-wizard/event-wizard-slice';
import {updatePreviewOrganiserDetails} from '@Components/event-wizard/preview/preview.helper';
import {PhoneNumberInput} from '@Components/phone-number-input';
import {MIN_PHONE_NUM_LENGTH, PhoneNumberInputSize} from '@Components/phone-number-input/phone-number-input.types';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {SocialMediaProfileLinks} from '@Components/social-media-profile-links';
import type {SocialMediaProfile} from '@Components/social-media-profile-links/social-media-profile-links.types';
import {
  formatSocialMediaLinks,
  initSocialMediaLinksErrorStates,
  initSocialMediaProfileLinks,
  validateSocialMediaLinks,
} from '@Components/social-media-profile-links/social-media-profile-links-library';
import {EventWizardErrorInfo} from '@Components/event-wizard/event-wizard-error-info';
import {EventDropdown} from '@Components/event-wizard/event-dropdown';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {ControlledList} from '@Components/controlled-list';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {getDefaultProfilePicture, getOrganiserProfilePicture} from '@Components/event-wizard/event-wizard-organisers/event-wizard-organisers.helper';
import styles from './event-wizard-organisers.module.scss';
import {openErrorModal} from '@Modals/error-modal';

export function EventWizardOrganisers(): ReactElement<any> {
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const shouldCheckErrState = useEventWizardShouldCheckErrState();
  const organisers = useEventWizardOrganisers();
  const {selectedOrganiser} = organisers;

  const [isProfilePicUploading, setProfilePicUploading] = useState<boolean>(false);
  const [socialMediaLinksErrState, setSocialMediaLinksErrState] = useState<Record<SocialMediaProfile, boolean>>(initSocialMediaLinksErrorStates());
  const [isOrganiserImgUploadDropdownOpen, setIsOrganiserImgUploadDropdownOpen] = useState<boolean>(false);

  useEffect(() => {
    validateSocialMediaLinksInput();
  }, []);

  const updateSelectedOrganiser = (organiserDetails: OrganiserDetails): void => {
    dispatch(onEventWizardSelectedOrganiserUpdate(organiserDetails));
    updatePreviewOrganiserDetails(organiserDetails);
  };

  const updateSelectedOrganiserFromPrefill = (organiserDetails: OrganiserDetails): void => {
    const updatedOrganiser: OrganiserDetails = {
      ...organiserDetails,
      isProfilePicturePrefilled: true,
    };
    updateSelectedOrganiser(updatedOrganiser);
  };

  const onUpdateName = (name: string): void => {
    const updatedOrganiser: OrganiserDetails = {
      ...selectedOrganiser,
      name,
    };
    updateSelectedOrganiser(updatedOrganiser);
  };

  const onUpdateEmail = (email: string): void => {
    const updatedOrganiser: OrganiserDetails = {
      ...selectedOrganiser,
      email,
    };
    updateSelectedOrganiser(updatedOrganiser);
  };

  const onUpdatePhone = (phone: string): void => {
    const updatedOrganiser: OrganiserDetails = {
      ...selectedOrganiser,
      phone: isValidPhoneNumber(phone) ? phone : undefined,
    };
    updateSelectedOrganiser(updatedOrganiser);
  };

  const onRemoveImage = (): void => {
    setProfilePicUploading(false);
    dispatch(onEventWizardShouldNextBtnBeDisabled(false));
    const updatedOrganiser: OrganiserDetails = {
      ...selectedOrganiser,
      profilePic: '',
      isProfilePicturePrefilled: false,
    };
    updateSelectedOrganiser(updatedOrganiser);
  };

  const onImageUpload = (input: ChangeEvent<HTMLInputElement>): void => {
    const file = input.target.files?.[0];

    if (!file) {
      input.target.value = '';
      return;
    }

    onImageUploadPreprocess();

    const formData = new FormData();
    formData.append('organiserPhoto', file);
    void window.PMW.writeLocal('event/temporaryUploadOrganiserPhoto', formData, 'json', {
      timeout: 7200000,
      cache: false,
      processData: false, // Don't process the files
      contentType: false, // Set content type to false as jQuery will tell the server it's a query string request
    })
      .then((response) => {
        onImageSuccessUpload(response as string);
      })
      .catch(() => {
        onImageFailedUpload();
      });
    input.target.value = '';
  };

  const onImageUploadPreprocess = (): void => {
    setProfilePicUploading(true);
    dispatch(onEventWizardShouldNextBtnBeDisabled(true));
  };

  const onImageSuccessUpload = (imgUrl: string): void => {
    const updatedOrganiser: OrganiserDetails = {
      ...selectedOrganiser,
      profilePic: imgUrl,
      isProfilePicturePrefilled: false,
    };
    updateSelectedOrganiser(updatedOrganiser);
    setProfilePicUploading(false);
    dispatch(onEventWizardShouldNextBtnBeDisabled(false));
  };

  const onImageFailedUpload = (): void => {
    setProfilePicUploading(false);
    dispatch(onEventWizardShouldNextBtnBeDisabled(false));
    openErrorModal({
      message: window.i18next.t('pmwjs_error_message'),
    });
  };

  const onUpdateSocialMediaProfileLinks = (formattedSocialMediaProfile: Record<SocialMediaProfile, string>): void => {
    updateSelectedOrganiser({
      ...selectedOrganiser,
      socialMediaLinks: formattedSocialMediaProfile,
    });
  };

  const onInputOfSocialMediaLink = (socialMediaProfile: SocialMediaProfile, url: string): void => {
    if (!selectedOrganiser.socialMediaLinks) {
      selectedOrganiser.socialMediaLinks = initSocialMediaProfileLinks();
    }
    const updatedSocialMediaLinks = {
      ...selectedOrganiser.socialMediaLinks,
      [socialMediaProfile]: url,
    };
    onUpdateSocialMediaProfileLinks(updatedSocialMediaLinks);
  };

  // codeReviewJibran: ideally we should do this at the PHP level but if we must, perhaps do this before we reach this component?
  const validateSocialMediaLinksInput = (): void => {
    const currentSocialMediaLinks = selectedOrganiser.socialMediaLinks;

    if (!currentSocialMediaLinks) {
      return;
    }
    const {errorMap, validatedUrls} = validateSocialMediaLinks(currentSocialMediaLinks);
    const formattedUrls = formatSocialMediaLinks(validatedUrls);
    setSocialMediaLinksErrState(errorMap);

    // Join formatted urls with error urls
    Object.entries(formattedUrls).forEach(([platform]) => {
      if (formattedUrls[platform as SocialMediaProfile] === '' && currentSocialMediaLinks[platform as SocialMediaProfile] !== '') {
        formattedUrls[platform as SocialMediaProfile] = currentSocialMediaLinks[platform as SocialMediaProfile];
      }
    });
    onUpdateSocialMediaProfileLinks(formattedUrls);
  };

  const getPhoneNumberInFormat = (): string => {
    const phoneNum = selectedOrganiser?.phone;

    if (!phoneNum || !isValidPhoneNumber(phoneNum)) {
      return '';
    }

    if (phoneNum.startsWith('+')) {
      return phoneNum;
    }
    return `+${phoneNum}`;
  };

  const getProfileImageContent = (): ReactElement<any> => {
    const getSelector = (): ReactElement<any> => {
      return (
        <div className={`${styles.profilePicSelectorContainer} flex-row-align-center justify-content-center border-s-dark radius-round _overflow-hidden _cursor-pointer`}>
          {isProfilePicUploading ? (
            <CircularProgressLoader size={LOADER_SIZE.SMALL} color={LOADER_COLOR.DARK} classes="_unmargin" />
          ) : (
            <>
              <Icon
                className={`${styles.profileImgUploadIcon} _position-absolute`}
                icon="icon-pencil"
                size={IconSize.SIZE_ICON_12}
                shape={IconShape.CIRCLE}
                type={IconType.SHADOW}
              />
              <img className="_full-width _full-height" src={getOrganiserProfilePicture(organisers.selectedOrganiser)} onError={(e) => {
                e.currentTarget.src = getDefaultProfilePicture()
              }} alt="Organiser profile" />
            </>
          )}
        </div>
      );
    };

    const getContent = (): ReactElement<any> => {
      return (
        <ControlledList
          id={organiserImgTypeDropdownID}
          className="spacing-m-0"
          items={[
            {
              id: 'organiser_img_change',
              type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
              icon: 'icon-upload',
              text: selectedOrganiser.profilePic !== '' ? window.i18next.t('pmwjs_change') : window.i18next.t('pmwjs_upload'),
              onClick(): void {
                uploadInputRef.current?.click();
              },
            },
            {
              id: 'organiser_img_remove',
              type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
              icon: 'icon-delete',
              text: window.i18next.t('pmwjs_delete'),
              textClasses: 'content-danger',
              iconClasses: styles.deleteListIcon,
              onClick(): void {
                onRemoveImage();
              },
            },
          ]}
        />
      );
    };

    return (
      <>
        <EventDropdown
          selector={getSelector()}
          popup={getContent()}
          className="flex-row-align-center justify-content-center spacing-m-r-3 _position-relative"
          popupClass={`${styles.profileImgUploadPopup} spacing-p-b-3`}
          alignment={ALIGNMENT_TYPE.RIGHT_END}
          open={isOrganiserImgUploadDropdownOpen}
          onOpen={() => {
            setIsOrganiserImgUploadDropdownOpen(true);
          }}
          onClose={() => {
            setIsOrganiserImgUploadDropdownOpen(false);
          }}
        />
        <input ref={uploadInputRef} type="file" className="_hidden" accept="image/gif, image/jpeg, image/png" onChange={onImageUpload} />
      </>
    );
  };

  const shouldDisplayNameError = (): boolean => {
    return shouldCheckErrState && selectedOrganiser.profilePic !== '' && selectedOrganiser.name === '';
  };

  const isValidPhoneNumber = (num: string): boolean => {
    return num.toString().length > MIN_PHONE_NUM_LENGTH;
  };

  return (
    <div>
      {organisers.allOrganisers.length > 0 ? (
        <div className="spacing-m-b-8">
          <EventWizardOrganiserOptions organisersDetails={organisers.allOrganisers} onClickOption={updateSelectedOrganiserFromPrefill} />
        </div>
      ) : undefined}
      <div className="flex-row-align-center align-items-start">
        {getProfileImageContent()}
        <div className="flex-column-align-start _full-width">
          <div className="flex-column-align-start spacing-m-b-2 _full-width">
            <Text className="spacing-m-b-1" val={window.i18next.t('pmwjs_name')} size={TextSize.XXSMALL} bold />
            <InputField
              state={shouldDisplayNameError() ? InputFieldState.ERROR : undefined}
              onInputChange={onUpdateName}
              placeholder={window.i18next.t('pmwjs_your_name')}
              input={selectedOrganiser?.name ?? ''}
              size={InputFieldSize.XSMALL}
            />
            {shouldDisplayNameError() ? <EventWizardErrorInfo text={window.i18next.t('pmwjs_please_enter_name')} className="spacing-m-t-1" /> : undefined}
          </div>
          <div className="flex-column-align-start spacing-m-b-2 _full-width">
            <Text className="spacing-m-b-1" val={window.i18next.t('pmwjs_email')} size={TextSize.XXSMALL} bold />
            <InputField
              onInputChange={onUpdateEmail}
              placeholder={window.i18next.t('pmwjs_your_email')}
              input={selectedOrganiser?.email ?? ''}
              size={InputFieldSize.XSMALL}
              inputType={InputFieldType.EMAIL}
            />
          </div>
          <div className="flex-column-align-start spacing-m-b-2 _full-width">
            <Text className="spacing-m-b-1" val={window.i18next.t('pmwjs_phone')} size={TextSize.XXSMALL} bold />
            <PhoneNumberInput
              className="_full-width"
              size={PhoneNumberInputSize.SMALL}
              phoneNumber={getPhoneNumberInFormat()}
              onInputChange={onUpdatePhone}
              disableDialCodePrefill
            />
          </div>
          <SocialMediaProfileLinks
            socialMediaProfileLinks={organisers.selectedOrganiser.socialMediaLinks}
            onInputChangeCallback={onInputOfSocialMediaLink}
            onInputBlurCallback={validateSocialMediaLinksInput}
            errorStates={socialMediaLinksErrState}
            containerClassName="spacing-m-t-4"
            classMoreOptionsDropdown="spacing-m-t-2"
          />
        </div>
      </div>
    </div>
  );
}
